import { Routes } from '@angular/router';
import { RouteSegments } from './constants/routes.const';
import { authGuard } from './guards/auth.guard';
import { guestGuard } from './guards/guest.guard';
import { dashboardMaintenanceResolver } from './pages/dashboard/maintenance-dashboard/maintenance-dashboard.resolver';
import { timeTrackingHolidaysResolver } from './pages/time-tracking/time-tracking-holidays.resolver';

export const routes: Routes = [
  {
    path: RouteSegments.Login,
    canActivate: [guestGuard],
    loadComponent: () => import('./pages/login/login.component').then(m => m.LoginComponent)
  },
  { path: '', redirectTo: RouteSegments.Dashboard, pathMatch: 'full' },
  {
    path: RouteSegments.Lp,
    loadComponent: () => import('./pages/landing/landing.component').then(m => m.LandingComponent)
  },
  {
    path: RouteSegments.RedefinirSenha,
    loadComponent: () => import('./pages/password-reset/password-reset.component').then(m => m.PasswordResetComponent)
  },
  {
    path: RouteSegments.Portal,
    loadComponent: () =>
      import('./pages/client-portal/client-portal.component').then(m => m.ClientPortalComponent)
  },
  {
    path: `${RouteSegments.Oportunidades}/:slug`,
    loadComponent: () =>
      import('./pages/opportunities/public-opportunity/public-opportunity.component').then(
        m => m.PublicOpportunityComponent
      )
  },
  {
    path: RouteSegments.Dashboard,
    canActivate: [authGuard],
    loadComponent: () =>
      import('./pages/dashboard/dashboard-shell/dashboard-shell.component').then(
        m => m.DashboardShellComponent
      ),
    children: [
      { path: '', redirectTo: RouteSegments.DashboardGeral, pathMatch: 'full' },
      {
        path: RouteSegments.DashboardGeral,
        resolve: { holidays: dashboardMaintenanceResolver },
        loadComponent: () =>
          import('./pages/dashboard/general-dashboard/general-dashboard.component').then(
            m => m.GeneralDashboardComponent
          )
      },
      {
        path: RouteSegments.DashboardDiscovery,
        loadComponent: () =>
          import('./pages/dashboard/discovery-dashboard/discovery-dashboard.component').then(
            m => m.DiscoveryDashboardComponent
          )
      },
      {
        path: RouteSegments.DashboardProjetos,
        loadComponent: () =>
          import('./pages/dashboard/projects-dashboard/projects-dashboard.component').then(
            m => m.ProjectsDashboardComponent
          )
      },
      {
        path: RouteSegments.DashboardSustentacao,
        resolve: {
          holidays: dashboardMaintenanceResolver,
        },
        loadComponent: () =>
          import('./pages/dashboard/maintenance-dashboard/maintenance-dashboard.component').then(
            m => m.MaintenanceDashboardComponent
          )
      }
    ]
  },
  {
    path: RouteSegments.Projetos,
    canActivate: [authGuard],
    loadComponent: () => import('./pages/projects/project-list/project-list.component').then(m => m.ProjectListComponent)
  },
  {
    path: RouteSegments.Cronograma,
    canActivate: [authGuard],
    loadComponent: () => import('./pages/schedule/schedule.component').then(m => m.ScheduleComponent)
  },
  {
    path: RouteSegments.Equipe,
    canActivate: [authGuard],
    loadComponent: () => import('./pages/team/team.component').then(m => m.TeamComponent)
  },
  {
    path: RouteSegments.OneOnOne,
    canActivate: [authGuard],
    data: { module: 'oneOnOne' },
    loadComponent: () => import('./pages/one-on-one/one-on-one.component').then(m => m.OneOnOneComponent)
  },
  {
    path: RouteSegments.Ouvidoria,
    canActivate: [authGuard],
    data: { module: 'ombudsman' },
    loadComponent: () =>
      import('./pages/ombudsman/ombudsman-shell/ombudsman-shell.component').then(
        m => m.OmbudsmanShellComponent
      ),
    children: [
      { path: '', redirectTo: RouteSegments.MinhasSolicitacoes, pathMatch: 'full' },
      {
        path: RouteSegments.MinhasSolicitacoes,
        loadComponent: () =>
          import('./pages/ombudsman/my-requests/my-requests.component').then(
            m => m.OmbudsmanMyRequestsComponent
          )
      },
      {
        path: RouteSegments.Atendimento,
        loadComponent: () =>
          import('./pages/ombudsman/service-desk/service-desk.component').then(
            m => m.OmbudsmanServiceDeskComponent
          )
      },
      {
        path: `${RouteSegments.Atendimento}/:requestId`,
        loadComponent: () =>
          import('./pages/ombudsman/service-desk/service-desk.component').then(
            m => m.OmbudsmanServiceDeskComponent
          )
      },
      {
        path: RouteSegments.Configuracoes,
        canActivate: [authGuard],
        data: { module: 'ombudsman', scope: 'settings' },
        loadComponent: () =>
          import('./pages/ombudsman/settings/settings.component').then(
            m => m.OmbudsmanSettingsComponent
          )
      }
    ]
  },
  {
    path: RouteSegments.Clientes,
    canActivate: [authGuard],
    loadComponent: () => import('./pages/clients/clients.component').then(m => m.ClientsComponent)
  },
  {
    path: `${RouteSegments.Projetos}/:id`,
    canActivate: [authGuard],
    loadComponent: () =>
      import('./pages/projects/shared/project-detail-shell/project-detail-shell.component').then(
        m => m.ProjectDetailShellComponent
      ),
    children: [
      {
        path: RouteSegments.VisaoGeral,
        loadComponent: () =>
          import('./pages/projects/shared/route-adapters/overview-tab-route/overview-tab-route.component').then(
            m => m.OverviewTabRouteComponent
          )
      },
      {
        path: RouteSegments.Etapas,
        loadComponent: () =>
          import('./pages/projects/shared/route-adapters/stages-tab-route/stages-tab-route.component').then(
            m => m.StagesTabRouteComponent
          )
      },
      {
        path: RouteSegments.Tarefas,
        loadComponent: () =>
          import('./pages/projects/shared/route-adapters/tasks-tab-route/tasks-tab-route.component').then(
            m => m.TasksTabRouteComponent
          )
      },
      {
        path: RouteSegments.Equipe,
        loadComponent: () =>
          import('./pages/projects/shared/route-adapters/team-tab-route/team-tab-route.component').then(
            m => m.TeamTabRouteComponent
          )
      },
      {
        path: RouteSegments.Comentarios,
        loadComponent: () =>
          import('./pages/projects/shared/route-adapters/comments-tab-route/comments-tab-route.component').then(
            m => m.CommentsTabRouteComponent
          )
      },
      {
        path: RouteSegments.Custos,
        loadComponent: () =>
          import('./pages/projects/shared/route-adapters/costs-tab-route/costs-tab-route.component').then(
            m => m.CostsTabRouteComponent
          )
      },
      {
        path: RouteSegments.Horas,
        loadComponent: () =>
          import('./pages/projects/shared/route-adapters/hours-tab-route/hours-tab-route.component').then(
            m => m.HoursTabRouteComponent
          )
      },
      {
        path: RouteSegments.Discovery,
        loadComponent: () =>
          import('./pages/projects/discovery/discovery-tab-shell/discovery-tab-shell.component').then(
            m => m.DiscoveryTabShellComponent
          ),
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./pages/projects/discovery/overview/discovery-overview.component').then(
                m => m.DiscoveryOverviewComponent
              )
          },
          {
            path: RouteSegments.Reunioes,
            loadComponent: () =>
              import('./pages/projects/discovery/meetings/discovery-meetings.component').then(
                m => m.DiscoveryMeetingsComponent
              )
          },
          {
            path: `${RouteSegments.Reunioes}/:meetingId`,
            loadComponent: () =>
              import('./pages/projects/discovery/meetings/discovery-meetings.component').then(
                m => m.DiscoveryMeetingsComponent
              )
          },
          {
            path: RouteSegments.Tarefas,
            loadComponent: () =>
              import('./pages/projects/discovery/tasks/project-tasks.component').then(
                m => m.ProjectDiscoveryTasksComponent
              )
          },
          {
            path: RouteSegments.Equipe,
            loadComponent: () =>
              import('./pages/projects/discovery/team/discovery-team.component').then(
                m => m.DiscoveryTeamComponent
              )
          },
          {
            path: RouteSegments.Comentarios,
            loadComponent: () =>
              import('./pages/projects/discovery/comments/discovery-comments.component').then(
                m => m.DiscoveryCommentsComponent
              )
          },
          {
            path: RouteSegments.Custos,
            loadComponent: () =>
              import('./pages/projects/discovery/costs/project-costs.component').then(
                m => m.ProjectDiscoveryCostsComponent
              )
          },
          {
            path: RouteSegments.Horas,
            loadComponent: () =>
              import('./pages/projects/discovery/hours/project-hours.component').then(
                m => m.ProjectDiscoveryHoursComponent
              )
          },
          {
            path: RouteSegments.Configuracoes,
            loadComponent: () =>
              import('./pages/projects/discovery/settings/discovery-settings.component').then(
                m => m.DiscoverySettingsComponent
              )
          }
        ]
      }
    ]
  },
  {
    path: RouteSegments.ControleDeHoras,
    canActivate: [authGuard],
    data: { module: 'timeTracking' },
    loadComponent: () =>
      import('./pages/time-tracking/time-tracking-shell/time-tracking-shell.component').then(
        m => m.TimeTrackingShellComponent
      ),
    children: [
      { path: '', redirectTo: RouteSegments.MeuMes, pathMatch: 'full' },
      {
        path: RouteSegments.MeuMes,
        resolve: { holidays: timeTrackingHolidaysResolver },
        loadComponent: () =>
          import('./pages/time-tracking/my-month/my-month.component').then(
            m => m.MyTimeTrackingMonthComponent
          )
      },
      {
        path: RouteSegments.MeuBanco,
        loadComponent: () =>
          import('./pages/time-tracking/my-bank/my-bank.component').then(
            m => m.MyTimeBankComponent
          )
      },
      {
        path: RouteSegments.MeuRecesso,
        loadComponent: () =>
          import('./pages/time-tracking/my-paid-leave/my-paid-leave.component').then(
            m => m.MyPaidLeaveComponent
          )
      },
      {
        path: RouteSegments.Recessos,
        canActivate: [authGuard],
        data: { module: 'timeTracking', scope: 'manage' },
        loadComponent: () =>
          import('./pages/time-tracking/admin-paid-leave/admin-paid-leave.component').then(
            m => m.AdminPaidLeaveComponent
          )
      },
      {
        path: RouteSegments.Conferencia,
        canActivate: [authGuard],
        data: { module: 'timeTracking', scope: 'review' },
        resolve: { holidays: timeTrackingHolidaysResolver },
        loadComponent: () =>
          import('./pages/time-tracking/review-queue/review-queue.component').then(
            m => m.TimeTrackingReviewQueueComponent
          )
      },
      {
        path: RouteSegments.Banco,
        canActivate: [authGuard],
        data: { module: 'timeTracking', scope: 'manage' },
        loadComponent: () =>
          import('./pages/time-tracking/admin-bank/admin-bank.component').then(
            m => m.TimeBankAdminComponent
          )
      },
      {
        path: RouteSegments.Configuracoes,
        canActivate: [authGuard],
        data: { module: 'timeTracking', scope: 'manage' },
        loadComponent: () =>
          import('./pages/time-tracking/settings/settings.component').then(
            m => m.TimeTrackingSettingsComponent
          )
      }
    ]
  },
  {
    path: RouteSegments.Recrutamento,
    canActivate: [authGuard],
    data: { module: 'recruitment' },
    loadComponent: () =>
      import('./pages/recruitment/recruitment-layout/recruitment-layout.component').then(
        m => m.RecruitmentLayoutComponent
      ),
    children: [
      { path: '', redirectTo: RouteSegments.Oportunidades, pathMatch: 'full' },
      {
        path: RouteSegments.Oportunidades,
        loadComponent: () =>
          import('./pages/recruitment/recruitment-list/recruitment-list.component').then(
            m => m.RecruitmentListComponent
          )
      },
      {
        path: `${RouteSegments.Oportunidades}/:id/${RouteSegments.Pipeline}`,
        loadComponent: () =>
          import('./pages/recruitment/recruitment-kanban/recruitment-kanban.component').then(
            m => m.RecruitmentKanbanComponent
          )
      },
      {
        path: `${RouteSegments.Oportunidades}/:id/${RouteSegments.Candidatos}`,
        redirectTo: `${RouteSegments.Oportunidades}/:id/${RouteSegments.Pipeline}`,
        pathMatch: 'full'
      },
      {
        path: RouteSegments.Agenda,
        loadComponent: () =>
          import('./pages/recruitment/recruitment-agenda/recruitment-agenda.component').then(
            m => m.RecruitmentAgendaComponent
          )
      },
      {
        path: RouteSegments.Configuracoes,
        loadComponent: () =>
          import('./pages/recruitment/recruitment-settings/recruitment-settings.component').then(
            m => m.RecruitmentSettingsComponent
          )
      },
      {
        path: `:id/${RouteSegments.Candidaturas}`,
        redirectTo: `${RouteSegments.Oportunidades}/:id/${RouteSegments.Pipeline}`,
        pathMatch: 'full'
      }
    ]
  },
  {
    path: RouteSegments.Configuracoes,
    canActivate: [authGuard],
    children: [
      { path: '', redirectTo: RouteSegments.Usuarios, pathMatch: 'full' },
      {
        path: RouteSegments.TemplatesEmail,
        canActivate: [authGuard],
        data: { module: 'recruitment' },
        loadComponent: () => import('./pages/settings/email-templates/email-templates.component').then(m => m.EmailTemplatesComponent)
      },
      {
        path: RouteSegments.Usuarios,
        loadComponent: () => import('./pages/settings/users/users.component').then(m => m.UserManagementComponent)
      },
      {
        path: `${RouteSegments.Usuarios}/:id`,
        loadComponent: () => import('./pages/settings/users/user-profile/user-profile.component').then(m => m.UserProfilePageComponent)
      },
      {
        path: RouteSegments.Cargos,
        loadComponent: () => import('./pages/settings/roles/roles.component').then(m => m.RoleManagementComponent)
      },
      {
        path: RouteSegments.Produtos,
        loadComponent: () => import('./pages/settings/products/products.component').then(m => m.ProductManagementComponent)
      }
    ]
  }
];
